import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Box, Typography, Card, CardContent, TextField, InputAdornment, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, Avatar, Button,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem, CircularProgress,
  Tooltip, Box as MuiBox,
} from '@mui/material';
import {
  Search as SearchIcon, LocalHospital as ProviderIcon, Add as AddIcon,
  Login as EnterIcon, WarningAmber as WarnIcon,
} from '@mui/icons-material';

import { PASSWORD_RULES_TEXT, passwordProblem, apiErrorMessage } from '../passwordPolicy';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const EMPTY = { clinic_id: '', full_name: '', email: '', password: '', speciality: '' };

export default function ProviderList() {
  const [providers, setProviders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);

  const [clinics, setClinics] = useState([]);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  // Acting as a provider writes to the record under their name, so it goes through
  // an explicit confirmation rather than a one-click action.
  const [enterTarget, setEnterTarget] = useState(null);
  const [entering, setEntering] = useState(false);

  const loadProviders = useCallback(() => {
    setLoading(true);
    axios.get(`${API_URL}/api/v1/superadmin/providers`)
      .then((res) => { setProviders(res.data.providers); setFiltered(res.data.providers); })
      .catch(() => toast.error('Failed to load providers'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  const confirmEnter = async () => {
    if (!enterTarget) return;
    setEntering(true);
    try {
      const res = await axios.post(`${API_URL}/api/v1/superadmin/providers/${enterTarget.id}/enter`);
      window.open(res.data.entry_url, '_blank', 'noopener');
      toast.success(`Opened ${res.data.clinic_name} as ${res.data.full_name || res.data.email}`);
      setEnterTarget(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not enter this account');
    } finally {
      setEntering(false);
    }
  };

  const openCreate = () => {
    setForm(EMPTY);
    setCreateOpen(true);
    if (clinics.length === 0) {
      axios.get(`${API_URL}/api/v1/superadmin/clinics`)
        .then((res) => setClinics(res.data.clinics || []))
        .catch(() => toast.error('Failed to load clinics'));
    }
  };

  const submitCreate = async () => {
    if (!form.clinic_id) { toast.error('Select a clinic'); return; }
    if (!form.full_name.trim() || !form.email.trim()) { toast.error('Name and email are required'); return; }
    const problem = passwordProblem(form.password);
    if (problem) { toast.error(problem); return; }
    setSaving(true);
    try {
      const payload = {
        clinic_id: form.clinic_id,
        full_name: form.full_name.trim(),
        email: form.email.trim(),
        password: form.password,
        speciality: form.speciality
          ? form.speciality.split(',').map((s) => s.trim()).filter(Boolean)
          : [],
      };
      await axios.post(`${API_URL}/api/v1/superadmin/providers`, payload);
      toast.success('Provider created — welcome email sent.');
      setCreateOpen(false);
      loadProviders();
    } catch (err) {
      toast.error(apiErrorMessage(err, 'Failed to create provider'));
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!searchTerm) { setFiltered(providers); return; }
    const q = searchTerm.toLowerCase();
    setFiltered(providers.filter((p) =>
      (p.full_name || '').toLowerCase().includes(q) ||
      (p.email || '').toLowerCase().includes(q) ||
      (p.clinic_name || '').toLowerCase().includes(q)
    ));
  }, [searchTerm, providers]);

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 48, height: 48 }}><ProviderIcon /></Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>All Providers</Typography>
          <Typography variant="body2" color="text.secondary">Doctors and clinical staff across all clinics</Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>Add Provider</Button>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <TextField fullWidth placeholder="Search by name, email, or clinic…" value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }}
          />
        </CardContent>
      </Card>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Clinic</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5}>Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5}>No providers found.</TableCell></TableRow>
            ) : filtered.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell><Typography fontWeight={600}>{p.full_name || '—'}</Typography></TableCell>
                <TableCell>{p.email}</TableCell>
                <TableCell>{p.clinic_name || '—'}</TableCell>
                <TableCell align="center">
                  <Chip size="small" label={p.is_active ? 'Active' : 'Inactive'} color={p.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <Tooltip title={p.is_active
                    ? 'Open the clinic portal signed in as this provider'
                    : 'This account is deactivated'}>
                    <span>
                      <Button size="small" variant="outlined" startIcon={<EnterIcon fontSize="small" />}
                        disabled={!p.is_active}
                        onClick={() => setEnterTarget(p)}>
                        Enter account
                      </Button>
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={Boolean(enterTarget)} onClose={() => { if (!entering) setEnterTarget(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Sign in as {enterTarget?.full_name || enterTarget?.email}?</DialogTitle>
        <DialogContent>
          <MuiBox sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <WarnIcon sx={{ color: '#b91c1c', mt: 0.25 }} />
            <MuiBox>
              <Typography variant="body2" sx={{ mb: 1 }}>
                You'll be signed into <strong>{enterTarget?.clinic_name}</strong> as this
                provider, seeing their queue, patients and sessions.
              </Typography>
              <Typography variant="body2" color="error" fontWeight={600}>
                This is their account, not yours. Anything you create — including clinical
                notes — is recorded under their name in the medical record.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Your access is written to the audit log and the session expires on its own.
              </Typography>
            </MuiBox>
          </MuiBox>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEnterTarget(null)} disabled={entering}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmEnter} disabled={entering}
            startIcon={entering ? <CircularProgress size={16} color="inherit" /> : <EnterIcon />}>
            {entering ? 'Opening…' : 'Sign in as them'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Provider</DialogTitle>
        <DialogContent>
          <TextField select fullWidth required label="Clinic" margin="normal"
            value={form.clinic_id} onChange={(e) => setForm({ ...form, clinic_id: e.target.value })}>
            {clinics.length === 0 && <MenuItem value="" disabled>No clinics found</MenuItem>}
            {clinics.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
          </TextField>
          <TextField fullWidth required label="Full Name" margin="normal"
            value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
          <TextField fullWidth required type="email" label="Email" margin="normal"
            value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <TextField fullWidth required type="password" label="Temporary Password" margin="normal"
            helperText={`${PASSWORD_RULES_TEXT} The provider is asked to choose their own when they first sign in.`}
            value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
          <TextField fullWidth label="Speciality (optional)" margin="normal"
            placeholder="e.g. Cardiology, Internal Medicine" helperText="Comma-separated"
            value={form.speciality} onChange={(e) => setForm({ ...form, speciality: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitCreate} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} /> : null}>
            {saving ? 'Creating…' : 'Create Provider'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
